import { IAddColumnBody } from '@makyoapp/makyo-sdk/api/Tables'
import { ColumnTypes, ITableColumn } from '@makyoapp/makyo-sdk/models/Table.js'

import { IDB } from '../db'
import { IDBTable } from '../table'

const ADVANCED_COLUMN_TYPES = [
  ColumnTypes.Connection,
  ColumnTypes.Lookup,
  ColumnTypes.Math,
  ColumnTypes.Template,
  ColumnTypes.SingleCell,
  ColumnTypes.Function,
]

export const syncSourceColumn = async (
  dbClient: IDB,
  table: IDBTable,
  column: ITableColumn,
  type: 'add' | 'delete'
) => {
  const queryTableIds = dbClient.queryRefMap.get(table.id)
  const snapshotTableIds = dbClient.snapshotRefMap.get(table.id)

  let syncColumnPromises: Promise<void>[] = []

  if (queryTableIds && queryTableIds.size > 0) {
    for (const queryTableId of queryTableIds) {
      const queryTable = dbClient.tablesMap.get(queryTableId)
      if (!queryTable) continue

      // get nature settings
      const natureSettings = queryTable.GetSnapshotSettings()
      if (!natureSettings) continue

      // update the query table
      syncColumnPromises.push(
        queryTable.UpdateQuerySettings(
          {
            ...natureSettings,
            _Columns:
              type === 'add'
                ? [...natureSettings._Columns, column._id]
                : natureSettings._Columns.filter((col) => col !== column._id),
          },
          undefined,
          false
        )
      )
    }
  }

  if (snapshotTableIds && snapshotTableIds.size > 0) {
    for (const snapshotTableId of snapshotTableIds) {
      const snapshotTable = dbClient.tablesMap.get(snapshotTableId)
      if (!snapshotTable) continue

      // get snapshot settings
      const natureSettings = snapshotTable.GetSnapshotSettings()
      if (!natureSettings) continue

      // update the snapshot table
      syncColumnPromises.push(
        snapshotTable.UpdateSnapshotSettings({
          ...natureSettings,
          _Columns:
            type === 'add'
              ? [...natureSettings._Columns, column._id]
              : natureSettings._Columns.filter((col) => col !== column._id),
        })
      )
    }
  }

  if (syncColumnPromises.length > 0) {
    await Promise.all(syncColumnPromises)
    window.dispatchEvent(new CustomEvent('db-client-reload'))
    return column
  }
}

export const syncQueryColumnAdd = async (table: IDBTable, payload: IAddColumnBody, hiddenByDefault = false) => {
  // if its a standard column types, add it to the source table instead
  if (!ADVANCED_COLUMN_TYPES.includes(payload._Type)) {
    const sourceTable = table.GetSnapshotSourceTable()
    if (sourceTable) {
      // add column to source table
      const newTableColumn = await sourceTable.AddColumn(payload, hiddenByDefault, true)

      // add new column id to query settings
      const querySettings = table.GetSnapshotSettings()

      await table.UpdateQuerySettings({
        ...querySettings,
        _Columns: [...querySettings._Columns, newTableColumn._id],
      })

      window.dispatchEvent(new CustomEvent('db-client-reload'))
      return newTableColumn
    }
  }
}

export const syncQueryColumnDuplicate = async (table: IDBTable, columnID: string, newColumnName: string) => {
  const columnToDuplicate = table.columnsMap.get(columnID)
  if (!columnToDuplicate) return

  // if its a standard column types, add it to the source table instead
  if (!ADVANCED_COLUMN_TYPES.includes(columnToDuplicate._Type)) {
    const sourceTable = table.GetSnapshotSourceTable()
    if (sourceTable) {
      // add column to source table
      const resDuplicateColumn = await sourceTable.DuplicateColumn(columnID, newColumnName)
      if (!resDuplicateColumn) return

      // add new column id to query settings
      const querySettings = table.GetSnapshotSettings()

      await table.UpdateQuerySettings({
        ...querySettings,
        _Columns: [...querySettings._Columns, resDuplicateColumn._id],
      })

      window.dispatchEvent(new CustomEvent('db-client-reload'))
      return resDuplicateColumn
    }
  }
}

export const syncQueryColumnDelete = async (table: IDBTable, columnID: string): Promise<boolean> => {
  const columnToDelete = table.columnsMap.get(columnID)
  if (!columnToDelete) return false

  // if its a standard column types, add it to the source table instead
  if (!ADVANCED_COLUMN_TYPES.includes(columnToDelete._Type)) {
    const sourceTable = table.GetSnapshotSourceTable()
    if (sourceTable) {
      // delete column from source table
      await sourceTable.DeleteColumn(columnID)

      // remove column id from query settings
      const querySettings = table.GetSnapshotSettings()
      await table.UpdateQuerySettings({
        ...querySettings,
        _Columns: querySettings._Columns.filter((col) => col !== columnID),
      })

      window.dispatchEvent(new CustomEvent('db-client-reload'))
      return true
    }
  }

  return false
}
